import React, { useState } from 'react';
import { connect } from 'react-redux';

import '../../../assets/css/player.css';
import '../css/user/feed.css';
import { showInfo } from '../../../shared/utils/swalHandler';
import useNavigation from '../../../shared/utils/useNavigation';
import { MessageIcon } from '../../../assets/Icons/SVG';
import {
  formatTimeAgo,
  formatDuration,
  isBundleContent,
  getMediaArray,
  getPrimaryMedia,
  isUserLoggedIn,
  isPostAccessible,
} from '../../../shared/utils/function';
import {
  DEFAULT_USER_AVATAR,
  MEDIA_TYPE,
  PLACEHOLDER_POST_IMAGE,
  MESSAGE_READ,
  TIP_TYPE
} from '../../../shared/constants/common';
import BundleSlider from "../../../features/slider/BundleSlider";
import LazyImage from '../../../components/LazyImage/LazyImage';
import VideoPlayer from '../../../shared/components/VideoPlayer';
import GridDropdown from '../../../features/cards/user/gridDropdown';
import LockedPPVContentPreview from './LockedPPVContentPreview';
import TipModal from '../../../views/Modals/TipModal';

const FeedPost = ({
  postId = null,
  post,
  creatorRole = null,
  isCreatorOwnProfile = false,
  creatorInfo = null,
  openDropdownId = null,
  onDropdownToggle = null,
  isShowDuration = false,
  showControls = false,
  isPPVContent = false,
  isSubContent = false,
  isShowChatButton = false,
  isShowEllipsis = false,
  onPurchaseSuccess = null,
  onOpenFollowModal = null,
  showPin = true,
  vatPercentage = 0,
  priority = false,
  // coming from mapStateToProps
  user = null,
}) => {
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoPlaybackStarted, setVideoPlaybackStarted] = useState(false);
  const [tipModalOpen, setTipModalOpen] = useState(false);
  const [tipModalData, setTipModalData] = useState(null);

  const likes = post.tips || 0;
  const isBundle = isBundleContent(post);
  const mediaArray = getMediaArray(post);
  const primaryMedia = getPrimaryMedia(post);

  const isSubscribed = isPostAccessible({
    post,
    isPPVContent,
    isSubContent,
    creatorInfo,
  });

  // Tip functionality functions
  const tipButton = (item) => {
    if (!isLoggedIn) {
      showInfo('', "Log in or Sign up to tip", {
        showCancelButton: true,
        confirmButtonText: 'Login',
      }).then((result) => {
        if (result.isConfirmed) {
          navigate.toLogin();
        }
      });
      return;
    }

    if (!creatorInfo) {
      return;
    }

    setTipModalData({
      creatorInfo: creatorInfo,
      tip_type: TIP_TYPE.CONTENT,
      item: item || post,
      issubscribed: isSubscribed,
    });
    setTipModalOpen(true);
  };

  const toggleTipModal = () => {
    setTipModalOpen((prevOpen) => {
      if (prevOpen) {
        // Clear data after a short delay to allow modal close animation
        setTimeout(() => {
          setTipModalData(null);
        }, 300);
      }
      return !prevOpen;
    });
  };

  const CaptionWithToggle = ({ text, maxLength = 100 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return null;

    const toggleReadMore = () => setIsExpanded(!isExpanded);

    return (
      <span className="caption-text">
        {isExpanded ? text : text.slice(0, maxLength) + (text.length > maxLength ? '...' : '')}
        {text.length > maxLength && (
          <span onClick={toggleReadMore} className="read-more-btn">
            {isExpanded ? MESSAGE_READ.SHOW_LESS_TEXT : MESSAGE_READ.SHOW_MORE_TEXT}
          </span>
        )}
      </span>
    );
  };

  const handleTagUser = (user) => {
    if (user.username) {
      window.location.href = `/${user.username}`;
    }
  };

  const onTipSuccess = (tipMessage) => {
    // Send the tip amount as a message
    // handleSendMessage({ message: tipMessage });
    onPurchaseSuccess(post); // refresh the post
  };

  const handleMessageClick = () => {
    if (!creatorInfo?.userid) return;
    navigate.toChat(creatorInfo?.userid);
  };

  return (
    <div className="modern-post-ui cm_modern_post_card" data-post-id={postId}>
      <div className="cm_modern_post_content">
        <div className='cm_pin_time_bage'>
          {post.pin && showPin && (
            <span className="cm_pin_badge">
              <i className='fas fa-thumbtack pinned-icon' title="Pinned" aria-label="Pinned"></i>
            </span>
          )}

          <span className="cm_timestamp_badge">
            {formatTimeAgo(post.created_at)}
          </span>
        </div>

        <div className="post_user_info">
          <LazyImage
            src={creatorInfo?.profile_picture || DEFAULT_USER_AVATAR}
            alt={creatorInfo?.username}
            className='cm-post-height'
            priority={priority}
            onError={(e) => {
              e.target.src = DEFAULT_USER_AVATAR;
            }}
          />
        </div>

        <div className='cm_modern_post_text'>
          <div
            className="username-section"
            onClick={() => handleTagUser(creatorInfo)}
          >
            <h6>@{creatorInfo?.username}</h6>
          </div>
          {post.caption && (
            <div className="cm_post_caption">
              <CaptionWithToggle
                text={post.caption}
                maxLength={100}
              />
            </div>
          )}

          {post.tag_users && post.tag_users.length > 0 && (
            <div className="tagged-users-section">
              {post.tag_users.map((user, index) => (
                <span key={index} className="tag-user" onClick={() => handleTagUser(user)}>
                  @{user.username || user}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="post-content">
        {isSubscribed && (
          <div className="post-media-container">
            {isBundle && mediaArray && mediaArray.length > 0 ? (
              <BundleSlider
                media={mediaArray}
                showControls={showControls}
                isLock={false}
                isShowDuration={isShowDuration}
              />
            ) : primaryMedia.type === MEDIA_TYPE.IMAGE ? (
              <LazyImage
                src={primaryMedia.src}
                alt="Post content"
                className='cm-post-height post-media'
                priority={priority}
                onError={(e) => {
                  e.target.src = PLACEHOLDER_POST_IMAGE;
                }}
              />
            ) : (
              <VideoPlayer
                src={primaryMedia.src}
                poster={primaryMedia.poster || null}
                className='cm-post-height video-no-timeline bundle-slider-media'
                showControls={showControls}
                controlsList="nodownload noplaybackrate noremoteplayback"
                disablePictureInPicture
                disableRemotePlayback
                preventContextMenu
                muted
                loop
                playsInline
                fallbackText='Video unavailable'
                showDurationBadge
                playbackStarted={videoPlaybackStarted}
                onPlayStarted={() => {
                  setIsVideoPlaying(true);
                  setVideoPlaybackStarted(true);
                }}
                onPause={() => setIsVideoPlaying(false)}
                onScrollPause={() => {
                  setVideoPlaybackStarted(false);
                  setIsVideoPlaying(false);
                }}
                pauseWhenOutOfView
              />
            )}

            {!isBundle &&
              isShowDuration &&
              post.type_file === MEDIA_TYPE.VIDEO &&
              post.duration &&
              !isVideoPlaying &&
              !videoPlaybackStarted && (
              <div className={`video_time_badge`}>
                <i className='fa-solid fa-video'></i>
                {formatDuration(post.duration)}
              </div>
            )}
          </div>
        )}

        {!isSubscribed && isPPVContent && (
          <LockedPPVContentPreview
            purchase_type='content'
            isBundle={isBundle}
            mediaArray={mediaArray}
            post={post}
            creatorInfo={creatorInfo}
            onUnlockClick={onPurchaseSuccess}
            isOpenFollowModal={() => onOpenFollowModal()}
            vatPercentage={vatPercentage}
          />
        )}
      </div>

      <div className="post-actions">
        <div className="actions-left">
          <div className="actions-group">
            <div className="like-section">
              <div className="like-icon">
                <i className='fas fa-heart'></i>
              </div>
              <span className="like-count">
                {likes.toLocaleString()}
              </span>
            </div>

            <div
              className="tips-display"
              onClick={() => tipButton(post)}
              style={{ cursor: 'pointer' }}
            >
              <div className="tips-icon">
                <i className="fa-solid fa-dollar-sign tips-icon-text"></i>
              </div>
              <span className="tips-amount">
                Tip
              </span>
            </div>

          </div>

          {isPPVContent && post?.isTrending && post?.isTrending === true && (
            <div className="trending-badge">
              <span className="trending-badge-text">
                Trending
              </span>
              <i className='fas fa-fire'></i>
            </div>
          )}
        </div>

        {isShowEllipsis && isSubscribed && (
          <GridDropdown
            className="cm_media_grid_dropdown"
            iconClass="fa-ellipsis-vertical"
            id={post?._id || null}
            creatorId={creatorInfo?._id || null}
            isOpen={openDropdownId === post?._id}
            onToggle={onDropdownToggle}
            isShowImpersonation={false}
            isSinglePost={true}
            postId={post?._id}
          />
        )}

        {isShowChatButton && creatorInfo?.username && (
          <button
            className="message-button"
            onClick={handleMessageClick}
            aria-label={`Message ${creatorInfo.username}`}
          >
            <MessageIcon />
          </button>
        )}
      </div>

      {/* Tip Modal */}
      {tipModalOpen && tipModalData && (
        <TipModal
          isOpen={tipModalOpen}
          toggle={toggleTipModal}
          creatorInfo={tipModalData.creatorInfo}
          tip_type={tipModalData.tip_type || TIP_TYPE.CONTENT}
          item={tipModalData.item}
          issubscribed={tipModalData.issubscribed}
          onTipSuccess={onTipSuccess}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
  };
};

export default connect(mapStateToProps)(FeedPost);
